import * as Yup from 'yup';
import {
    Autocomplete,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    FormHelperText,
    Grid,
    InputAdornment,
    Paper,
    Stack,
    Switch,
    TextField,
    Typography
} from '@mui/material';
import React, { useEffect, useMemo } from 'react';
import { OpenModalHookType } from '@hooks/useOpenModal';
import { Controller, SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { formatUnitCost, getUnitCost, Input } from '@api/Inputs/Input';
import { buildInputsById, getRecipeCost, getRecipeCostSale, Recipe, RecipeItem } from '@api/Recipes/Recipe';
import { CostSummaryBar } from '@screens/Catalog/components/CostSummaryBar';
import { EmptyItemsBox } from '@screens/Catalog/components/EmptyItemsBox';

interface ModalSaveRecipeProps {
    modalSaveRecipe: OpenModalHookType;
    recipe?: Recipe;
    inputsList: Input[];
    onSaveRecipe: (recipe: Recipe) => void;
}

const defaultRecipeValues: any = {
    id: undefined,
    name: '',
    items: [],
    availableForModifiers: false,
    status: true
};

const saveRecipeSchema = Yup.object({
    id: Yup.string().nullable(),
    name: Yup.string().required('Nombre requerido'),
    availableForModifiers: Yup.boolean(),
    status: Yup.boolean(),
    items: Yup.array()
        .of(
            Yup.object().shape({
                inputId: Yup.string().required('Insumo requerido'),
                quantity: Yup.number()
                    .typeError('Cantidad requerida')
                    .required('Cantidad requerida')
                    .moreThan(0, 'Debe ser mayor a 0'),
                optional: Yup.boolean()
            })
        )
        .min(1, 'Agrega al menos un insumo a la receta')
});

export const ModalSaveRecipe: React.FunctionComponent<ModalSaveRecipeProps> = (props) => {
    const { modalSaveRecipe, recipe, inputsList, onSaveRecipe } = props;

    // Orden por categoría y nombre: el Autocomplete agrupa por categoría
    const sortedInputs = useMemo(
        () =>
            [...inputsList].sort(
                (a, b) =>
                    (a.inputCategoryData?.name || 'Sin categoría').localeCompare(
                        b.inputCategoryData?.name || 'Sin categoría',
                        'es'
                    ) || a.name.localeCompare(b.name, 'es')
            ),
        [inputsList]
    );
    const inputsById = useMemo(() => buildInputsById(inputsList), [inputsList]);

    const {
        control,
        handleSubmit,
        reset,
        watch,
        formState: { errors }
    } = useForm<Recipe>({
        resolver: yupResolver(saveRecipeSchema),
        defaultValues: defaultRecipeValues
    });

    const {
        fields: itemFields,
        append: appendItem,
        remove: removeItem
    } = useFieldArray({
        control,
        name: 'items'
    });

    // Costeo en vivo mientras se captura (misma info que persistirá RecipeServ.saveRecipe)
    const watchedItems = (watch('items') || []) as RecipeItem[];
    const liveCost = getRecipeCost(watchedItems, inputsById);
    const liveCostSale = getRecipeCostSale(watchedItems, inputsById);

    const addItem = () => appendItem({ inputId: '', quantity: 0, optional: false });

    const handleClose = () => {
        reset(defaultRecipeValues);
        modalSaveRecipe.toggle();
    };

    const onSaveRecipeForm: SubmitHandler<Recipe> = (values) => {
        onSaveRecipe(values);
    };

    useEffect(() => {
        if (modalSaveRecipe.modalOpen) {
            if (recipe && recipe.id) {
                reset({
                    id: recipe.id,
                    name: recipe.name || '',
                    availableForModifiers: recipe.availableForModifiers ?? false,
                    status: recipe.status ?? true,
                    items: recipe.items
                        ? recipe.items.map((item) => ({
                              inputId: item.inputId,
                              quantity: item.quantity || 0,
                              optional: item.optional ?? false
                          }))
                        : []
                });
            } else {
                reset(defaultRecipeValues);
            }
        }
    }, [modalSaveRecipe.modalOpen, recipe, reset]);

    return (
        <Dialog
            PaperProps={{
                style: { borderRadius: 15, padding: 4 }
            }}
            open={modalSaveRecipe.modalOpen}
            onClose={handleClose}
            aria-labelledby="modal-save-recipe-title"
            fullWidth
            maxWidth="md"
        >
            <DialogTitle id="modal-save-recipe-title">
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                        <Typography variant="h6" fontWeight={600}>
                            {watch('id') ? 'Editar receta' : 'Nueva receta'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Lista de insumos (en su unidad base) que consume una preparación
                        </Typography>
                    </Box>
                    <IconButton color="secondary" onClick={handleClose}>
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent>
                <form id="save-recipe-form" onSubmit={handleSubmit(onSaveRecipeForm)}>
                    <Grid container spacing={2} pt={1}>
                        <Grid item xs={12} md={6}>
                            <Controller
                                name="name"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        required
                                        label="Nombre"
                                        variant="outlined"
                                        placeholder='Ej. "Porción de BBQ", "Hamburguesa sencilla"'
                                        error={!!errors.name}
                                        helperText={errors.name?.message}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Stack
                                direction="row"
                                spacing={2}
                                height="100%"
                                alignItems="center"
                                justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
                            >
                                <Controller
                                    name="availableForModifiers"
                                    control={control}
                                    render={({ field: { onChange, value } }) => (
                                        <FormControlLabel
                                            control={<Switch checked={!!value} onChange={onChange} />}
                                            label="Para modificadores"
                                        />
                                    )}
                                />
                                <Controller
                                    name="status"
                                    control={control}
                                    render={({ field: { onChange, value } }) => (
                                        <FormControlLabel
                                            control={<Switch checked={!!value} onChange={onChange} />}
                                            label="Activa"
                                        />
                                    )}
                                />
                            </Stack>
                        </Grid>
                        <Grid item xs={12}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Typography fontSize={17} fontWeight={600}>
                                    Insumos de la receta
                                </Typography>
                                {itemFields.length > 0 && (
                                    <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={addItem}>
                                        Agregar insumo
                                    </Button>
                                )}
                            </Stack>
                            {errors.items && !Array.isArray(errors.items) && (
                                <FormHelperText error>{(errors.items as any)?.message}</FormHelperText>
                            )}
                        </Grid>
                        <Grid item xs={12}>
                            {itemFields.length === 0 && (
                                <EmptyItemsBox
                                    text="Esta receta aún no tiene insumos"
                                    buttonLabel="Agregar insumo"
                                    onAdd={addItem}
                                />
                            )}
                            {itemFields.map((field, index) => {
                                const currentInputId = watch(`items.${index}.inputId`);
                                const currentInput = currentInputId ? inputsById.get(currentInputId) : undefined;
                                const missingInput = !!currentInputId && !currentInput;
                                const unitCost = currentInput ? currentInput.unitCost ?? getUnitCost(currentInput) : 0;
                                const quantity = Number(watch(`items.${index}.quantity`)) || 0;
                                // Opción sintética para insumos borrados: se muestra el hueco y se exige reemplazo
                                const options: Input[] = missingInput
                                    ? [
                                          {
                                              id: currentInputId,
                                              name: `Insumo eliminado (${currentInputId})`
                                          } as Input,
                                          ...sortedInputs
                                      ]
                                    : sortedInputs;
                                const valueOption = options.find((option) => option.id === currentInputId) || null;

                                return (
                                    <Paper key={field.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2, mb: 1.5 }}>
                                        <Grid container spacing={2} alignItems="center">
                                            <Grid item xs={12} md={5}>
                                                <Controller
                                                    name={`items.${index}.inputId`}
                                                    control={control}
                                                    render={({ field: { onChange } }) => (
                                                        <Autocomplete
                                                            options={options}
                                                            value={valueOption}
                                                            onChange={(_, option) => onChange(option?.id || '')}
                                                            getOptionLabel={(option) => option.name}
                                                            isOptionEqualToValue={(a, b) => a.id === b.id}
                                                            groupBy={(option) =>
                                                                option.inputCategoryData?.name || 'Sin categoría'
                                                            }
                                                            noOptionsText="Sin resultados"
                                                            renderOption={(props, option) => (
                                                                <li {...props} key={option.id}>
                                                                    <Stack>
                                                                        <Typography variant="body2">
                                                                            {option.name}
                                                                        </Typography>
                                                                        {option.unit && (
                                                                            <Typography
                                                                                variant="caption"
                                                                                color="text.secondary"
                                                                            >
                                                                                {formatUnitCost(
                                                                                    option.unitCost ??
                                                                                        getUnitCost(option)
                                                                                )}{' '}
                                                                                / {option.unit.unit}
                                                                            </Typography>
                                                                        )}
                                                                    </Stack>
                                                                </li>
                                                            )}
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    {...params}
                                                                    label="Insumo"
                                                                    required
                                                                    error={
                                                                        !!errors.items?.[index]?.inputId ||
                                                                        missingInput
                                                                    }
                                                                    helperText={
                                                                        errors.items?.[index]?.inputId?.message ||
                                                                        (missingInput &&
                                                                            'Este insumo ya no existe: elige uno vigente')
                                                                    }
                                                                />
                                                            )}
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                            <Grid item xs={6} md={3}>
                                                <Controller
                                                    name={`items.${index}.quantity`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <TextField
                                                            {...field}
                                                            fullWidth
                                                            required
                                                            type="number"
                                                            label="Cantidad"
                                                            inputProps={{ step: 'any', min: 0 }}
                                                            InputProps={{
                                                                endAdornment: currentInput ? (
                                                                    <InputAdornment position="end">
                                                                        {currentInput.unit.unit}
                                                                    </InputAdornment>
                                                                ) : undefined
                                                            }}
                                                            error={!!errors.items?.[index]?.quantity}
                                                            helperText={
                                                                errors.items?.[index]?.quantity?.message ||
                                                                (currentInput &&
                                                                    `${formatUnitCost(unitCost)} / ${
                                                                        currentInput.unit.unit
                                                                    }`)
                                                            }
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                            <Grid item xs={6} md={2.5}>
                                                <Controller
                                                    name={`items.${index}.optional`}
                                                    control={control}
                                                    render={({ field: { onChange, value } }) => (
                                                        <FormControlLabel
                                                            control={<Switch checked={!!value} onChange={onChange} />}
                                                            label={
                                                                <Typography variant="body2">Se puede quitar</Typography>
                                                            }
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={1.5}>
                                                <Stack
                                                    direction={{ xs: 'row', md: 'column' }}
                                                    alignItems={{ xs: 'center', md: 'flex-end' }}
                                                    justifyContent="space-between"
                                                    spacing={0.5}
                                                >
                                                    <Typography variant="body2" fontWeight={700}>
                                                        {formatUnitCost(quantity * unitCost)}
                                                    </Typography>
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => removeItem(index)}
                                                    >
                                                        <DeleteOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                </Stack>
                                            </Grid>
                                        </Grid>
                                    </Paper>
                                );
                            })}
                        </Grid>
                    </Grid>
                </form>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Stack width="100%" spacing={1.5}>
                    <CostSummaryBar
                        items={[
                            { label: 'Costo (compra)', value: formatUnitCost(liveCost) },
                            { label: 'Costo (traspaso a sucursal)', value: formatUnitCost(liveCostSale) },
                            { label: 'Insumos', value: `${watchedItems.length}` }
                        ]}
                    />
                    <Box textAlign="center">
                        <Button
                            sx={{ width: 300 }}
                            color="primary"
                            variant="contained"
                            type="submit"
                            form="save-recipe-form"
                        >
                            Guardar
                        </Button>
                    </Box>
                </Stack>
            </DialogActions>
        </Dialog>
    );
};
